import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Search,
  Loader2,
  Building2,
  User,
  Phone,
  Mail,
  CalendarDays,
  Euro,
  ClipboardCheck,
  ExternalLink,
  X,
  Filter,
  RefreshCw,
  Lock
} from 'lucide-react';

const MONTHS_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

const WEEKDAYS_SHORT = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

const SynopticCalendar = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ properties: [], bookings: [], closures: [], days: [] });
  const [viewType, setViewType] = useState('month'); // month, quarter, year
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [selectedResponsibleId, setSelectedResponsibleId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, past
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [responsabili, setResponsabili] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch responsabili for filter (admin only)
  useEffect(() => {
    const fetchResponsabili = async () => {
      try {
        const token = localStorage.getItem('hw_token');
        const response = await axios.get(`${API}/api/property-managers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResponsabili(response.data || []);
      } catch (error) {
        // Ignore 403 errors - user doesn't have admin privileges
        if (error.response?.status !== 403) {
          console.error('Error fetching responsabili:', error);
        }
      }
    };
    
    const fetchProperties = async () => {
      try {
        const token = localStorage.getItem('hw_token');
        const response = await axios.get(`${API}/api/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllProperties(response.data || []);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };
    
    fetchResponsabili();
    fetchProperties();
  }, []);

  // Calculate start date based on view type and current date
  const getStartDate = useCallback(() => {
    const d = new Date(currentDate);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  }, [currentDate]);

  // Fetch calendar data
  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      
      const params = new URLSearchParams({
        start_date: getStartDate(),
        view_type: viewType,
        status: statusFilter
      });
      
      if (selectedPropertyIds.length > 0) {
        params.append('property_ids', selectedPropertyIds.join(','));
      }
      if (selectedResponsibleId) {
        params.append('responsible_id', selectedResponsibleId);
      }
      if (searchTerm) {
        params.append('guest_search', searchTerm);
      }
      
      const response = await axios.get(`${API}/api/calendar/synoptic?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setData(response.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  }, [getStartDate, viewType, statusFilter, selectedPropertyIds, selectedResponsibleId, searchTerm]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // Navigate months
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (viewType === 'quarter') {
      newDate.setMonth(newDate.getMonth() + (direction * 3));
    } else {
      newDate.setFullYear(newDate.getFullYear() + direction);
    }
    setCurrentDate(newDate);
  };

  // Get booking/closure for a specific property and day
  const getCellData = useCallback((propertyId, dayDate) => {
    const bookingsForCell = data.bookings.filter(b => {
      if (b.property_id !== propertyId) return false;
      const checkIn = b.check_in?.split('T')[0];
      const checkOut = b.check_out?.split('T')[0];
      return dayDate >= checkIn && dayDate < checkOut;
    });
    
    const closuresForCell = data.closures.filter(c => {
      if (c.property_id !== propertyId) return false;
      const start = c.start_date?.split('T')[0];
      const end = c.end_date?.split('T')[0];
      return dayDate >= start && dayDate <= end;
    });
    
    return { bookings: bookingsForCell, closures: closuresForCell };
  }, [data.bookings, data.closures]);

  // Determine cell color
  const getCellColor = (cellData, dayDate) => {
    const today = new Date().toISOString().split('T')[0];
    
    if (cellData.closures.length > 0) {
      return 'bg-orange-400 hover:bg-orange-500'; // Chiusura
    }
    
    if (cellData.bookings.length > 0) {
      const booking = cellData.bookings[0];
      if (booking.is_past || dayDate < today) {
        return 'bg-green-500 hover:bg-green-600'; // Passata
      }
      return 'bg-red-500 hover:bg-red-600'; // Attiva
    }
    
    return ''; // Vuota
  };

  // Fetch booking details for popup
  const fetchBookingDetails = async (bookingId) => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/calendar/booking/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Flatten the response for easier use
      const data = response.data;
      setBookingDetails({
        ...data.booking,
        property: data.property,
        precheckin_status: data.precheckin_status,
        is_past: data.is_past,
        precheckin_complete: data.precheckin_status?.pending === 0 && data.precheckin_status?.total_guests > 0
      });
    } catch (error) {
      console.error('Error fetching booking details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handle cell click
  const handleCellClick = (cellData, propertyId, dayDate) => {
    if (cellData.bookings.length > 0) {
      const booking = cellData.bookings[0];
      setSelectedBooking({ ...booking, propertyId, dayDate });
      fetchBookingDetails(booking.id);
    } else if (cellData.closures.length > 0) {
      // Show closure info
      setSelectedBooking({ 
        type: 'closure', 
        ...cellData.closures[0],
        propertyId, 
        dayDate 
      });
      setBookingDetails(null);
    }
  };

  // Get navigation label
  const getNavigationLabel = () => {
    if (viewType === 'year') {
      return currentDate.getFullYear().toString();
    }
    if (viewType === 'quarter') {
      const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
      return `Q${quarter} ${currentDate.getFullYear()}`;
    }
    return `${MONTHS_IT[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  // Group days by month for display
  const groupedDays = useMemo(() => {
    const groups = [];
    let currentMonth = null;
    let currentGroup = null;
    
    data.days.forEach(day => {
      if (day.month !== currentMonth) {
        if (currentGroup) groups.push(currentGroup);
        currentMonth = day.month;
        currentGroup = {
          month: day.month,
          year: day.year,
          label: `${MONTHS_IT[day.month - 1]} ${day.year}`,
          days: []
        };
      }
      currentGroup.days.push(day);
    });
    
    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [data.days]);

  // Property filter selection
  const togglePropertyFilter = (propId) => {
    setSelectedPropertyIds(prev => 
      prev.includes(propId) 
        ? prev.filter(id => id !== propId)
        : [...prev, propId]
    );
  };

  if (loading && data.properties.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="synoptic-calendar-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-slate-600">Caricamento calendario...</span>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="synoptic-calendar">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            Calendario Sinottico
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {data.summary?.total_properties || 0} immobili | {data.summary?.total_bookings || 0} prenotazioni | {data.summary?.total_closures || 0} chiusure
          </p>
        </div>
        
        {/* View Type & Navigation */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateMonth(-1)}
            data-testid="navigate-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="min-w-[180px] text-center font-semibold text-slate-700">
            {getNavigationLabel()}
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateMonth(1)}
            data-testid="navigate-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          
          <Select value={viewType} onValueChange={setViewType}>
            <SelectTrigger className="w-[120px]" data-testid="view-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Mese</SelectItem>
              <SelectItem value="quarter">Trimestre</SelectItem>
              <SelectItem value="year">Anno</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-blue-50' : ''}
          >
            <Filter className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={fetchCalendarData}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card data-testid="filters-panel">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search Guest */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Cerca Ospite</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder="Nome, email, telefono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="guest-search"
                />
              </div>
            </div>
            
            {/* Responsabile Filter */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Responsabile</label>
              <Select value={selectedResponsibleId || 'all'} onValueChange={(val) => setSelectedResponsibleId(val === 'all' ? '' : val)}>
                <SelectTrigger data-testid="responsible-filter">
                  <SelectValue placeholder="Tutti i responsabili" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i responsabili</SelectItem>
                  {responsabili.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Stato Prenotazioni</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="active">Attive</SelectItem>
                  <SelectItem value="past">Passate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Property Multi-Select (simplified) */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Immobili ({selectedPropertyIds.length > 0 ? selectedPropertyIds.length : 'Tutti'})
              </label>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setSelectedPropertyIds([])}
                data-testid="clear-property-filter"
              >
                {selectedPropertyIds.length > 0 ? 'Rimuovi filtri immobili' : 'Tutti gli immobili'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-red-500"></div>
          <span className="text-slate-600">Prenotazione attiva</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-green-500"></div>
          <span className="text-slate-600">Prenotazione passata</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-orange-400"></div>
          <span className="text-slate-600">Chiusura</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          <div className="relative overflow-auto max-h-[calc(100vh-300px)]" style={{ maxWidth: '100%' }}>
            <table className="border-collapse" style={{ tableLayout: 'fixed' }}>
              {/* Sticky Header */}
              <thead className="sticky top-0 z-30 bg-white">
                {/* Month Names Row */}
                <tr>
                  <th 
                    className="sticky left-0 z-40 bg-slate-100 border-r border-b p-2 min-w-[200px] w-[200px]"
                    rowSpan={2}
                    style={{ boxShadow: '2px 0 5px -2px rgba(0,0,0,0.15)' }}
                  >
                    <div className="flex items-center font-semibold text-slate-700">
                      <Building2 className="h-4 w-4 mr-2" />
                      Immobile
                    </div>
                  </th>
                  {groupedDays.map((monthGroup, idx) => (
                    <th 
                      key={`month-${idx}`}
                      colSpan={monthGroup.days.length}
                      className="bg-slate-100 border-r border-b text-center font-semibold text-slate-700 py-1 text-sm"
                    >
                      {monthGroup.label}
                    </th>
                  ))}
                </tr>
                {/* Day Numbers Row */}
                <tr>
                  {groupedDays.map((monthGroup, monthIdx) => (
                    monthGroup.days.map((day, dayIdx) => (
                      <th 
                        key={`day-${monthIdx}-${dayIdx}`}
                        className={`w-7 min-w-[28px] text-center py-1 text-xs border-r border-b ${day.is_weekend ? 'bg-slate-200' : 'bg-slate-50'}`}
                      >
                        <div className="text-[10px] text-slate-500">{WEEKDAYS_SHORT[day.weekday]}</div>
                        <div className={day.is_weekend ? 'text-slate-600' : 'text-slate-800'}>{day.day}</div>
                      </th>
                    ))
                  ))}
                </tr>
              </thead>

              {/* Property Rows */}
              <tbody>
                {data.properties.length === 0 ? (
                  <tr>
                    <td colSpan={data.days.length + 1} className="p-8 text-center text-slate-500">
                      <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                      <p>Nessun immobile trovato con i filtri selezionati</p>
                    </td>
                  </tr>
                ) : (
                  data.properties.map((property, propIdx) => (
                    <tr 
                      key={property.id}
                      className={`hover:bg-blue-50 ${propIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                      data-testid={`property-row-${property.id}`}
                    >
                      {/* Sticky Property Name Column */}
                      <td 
                        className="sticky left-0 z-20 bg-inherit border-r p-2 cursor-pointer min-w-[200px] w-[200px]"
                        onClick={() => togglePropertyFilter(property.id)}
                        title={`${property.name}\nClick per filtrare`}
                        style={{ 
                          backgroundColor: propIdx % 2 === 0 ? 'white' : '#f8fafc',
                          boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)'
                        }}
                      >
                        <div className="truncate">
                          <div className="font-medium text-sm text-slate-800 truncate">
                            {property.name}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {property.city || property.property_code}
                          </div>
                        </div>
                      </td>
                      
                      {/* Day Cells */}
                      {groupedDays.map((monthGroup) => (
                        monthGroup.days.map((day, dayIdx) => {
                          const cellData = getCellData(property.id, day.date);
                          const cellColor = getCellColor(cellData, day.date);
                          const hasData = cellData.bookings.length > 0 || cellData.closures.length > 0;
                          
                          return (
                            <td
                              key={`cell-${property.id}-${day.date}`}
                              className={`w-7 min-w-[28px] h-8 border-r border-b border-slate-100 cursor-pointer transition-colors ${cellColor} ${day.is_weekend && !hasData ? 'bg-slate-50' : ''}`}
                              onClick={() => hasData && handleCellClick(cellData, property.id, day.date)}
                              title={hasData ? (cellData.bookings[0]?.guest_name || 'Chiusura') : ''}
                              data-testid={`cell-${property.id}-${day.date}`}
                              data-has-booking={cellData.bookings.length > 0 ? 'true' : undefined}
                              data-has-closure={cellData.closures.length > 0 ? 'true' : undefined}
                            >
                              {cellData.closures.length > 0 && (
                                <div className="h-full flex items-center justify-center">
                                  <Lock className="h-3 w-3 text-white" />
                                </div>
                              )}
                            </td>
                          );
                        })
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Booking/Closure Detail Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => { setSelectedBooking(null); setBookingDetails(null); }}>
        <DialogContent className="max-w-lg" data-testid="booking-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedBooking?.type === 'closure' ? (
                <>
                  <Lock className="h-5 w-5 text-orange-500" />
                  Chiusura Proprietà
                </>
              ) : (
                <>
                  <CalendarDays className="h-5 w-5 text-blue-500" />
                  Dettagli Prenotazione
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking?.type === 'closure' ? (
            // Closure Details
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-4 w-4 text-orange-600" />
                  <span className="font-semibold text-orange-800">{selectedBooking.property_name}</span>
                </div>
                <div className="text-sm text-orange-700 space-y-1">
                  <p><strong>Dal:</strong> {new Date(selectedBooking.start_date).toLocaleDateString('it-IT')}</p>
                  <p><strong>Al:</strong> {new Date(selectedBooking.end_date).toLocaleDateString('it-IT')}</p>
                  {selectedBooking.reason && (
                    <p><strong>Motivo:</strong> {selectedBooking.reason}</p>
                  )}
                </div>
              </div>
            </div>
          ) : loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-slate-600">Caricamento dettagli...</span>
            </div>
          ) : bookingDetails ? (
            // Booking Details
            <div className="space-y-4">
              {/* Guest Info */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <User className="h-4 w-4 text-blue-600" />
                  <span className="font-semibold text-blue-800">{bookingDetails.guest_name || 'Ospite'}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {bookingDetails.guest_phone && (
                    <a href={`tel:${bookingDetails.guest_phone}`} className="flex items-center gap-2 text-blue-700 hover:underline">
                      <Phone className="h-3 w-3" />
                      {bookingDetails.guest_phone}
                    </a>
                  )}
                  {bookingDetails.guest_email && (
                    <a href={`mailto:${bookingDetails.guest_email}`} className="flex items-center gap-2 text-blue-700 hover:underline">
                      <Mail className="h-3 w-3" />
                      {bookingDetails.guest_email}
                    </a>
                  )}
                </div>
              </div>
              
              {/* Booking Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Check-in</div>
                  <div className="font-semibold text-slate-800">
                    {bookingDetails.check_in ? new Date(bookingDetails.check_in).toLocaleDateString('it-IT') : '-'}
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Check-out</div>
                  <div className="font-semibold text-slate-800">
                    {bookingDetails.check_out ? new Date(bookingDetails.check_out).toLocaleDateString('it-IT') : '-'}
                  </div>
                </div>
              </div>
              
              {/* Costi Servizi - Solo Pulizia e Biancheria (NON prezzo totale) */}
              {(bookingDetails.cleaning_fee > 0 || bookingDetails.linen_fee > 0) && (
                <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                  <div className="text-sm font-medium text-slate-700">Costi Servizi</div>
                  {bookingDetails.cleaning_fee > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Pulizia</span>
                      <span className="font-medium">€ {bookingDetails.cleaning_fee.toFixed(2)}</span>
                    </div>
                  )}
                  {bookingDetails.linen_fee > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Biancheria</span>
                      <span className="font-medium">€ {bookingDetails.linen_fee.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Status Badge */}
              <div className="flex items-center justify-center">
                <Badge className={bookingDetails.is_past ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {bookingDetails.is_past ? 'Completata' : 'In corso'}
                </Badge>
              </div>
              
              {/* Precheckin Status */}
              <div className="flex items-center gap-2 p-3 rounded-lg border">
                <ClipboardCheck className={`h-4 w-4 ${bookingDetails.precheckin_complete ? 'text-green-500' : 'text-orange-500'}`} />
                <span className="text-sm">
                  Precheckin: <strong className={bookingDetails.precheckin_complete ? 'text-green-600' : 'text-orange-600'}>
                    {bookingDetails.precheckin_complete ? 'Completato' : 'Da completare'}
                  </strong>
                </span>
                {bookingDetails.guests?.length > 0 && (
                  <Badge variant="outline" className="ml-auto">
                    {bookingDetails.guests.length} ospiti
                  </Badge>
                )}
              </div>
              
              {/* Property Info */}
              {bookingDetails.property && (
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Building2 className="h-4 w-4" />
                    <span className="font-medium">{bookingDetails.property.name}</span>
                  </div>
                  {bookingDetails.property.city && (
                    <div className="text-xs text-slate-500 mt-1 ml-6">
                      {bookingDetails.property.city}
                    </div>
                  )}
                </div>
              )}
              
              {/* WhatsApp Button - Centrato e Verde */}
              {bookingDetails.guest_phone && (
                <div className="flex justify-center pt-2 border-t">
                  <Button
                    className="bg-green-500 hover:bg-green-600 text-white px-6"
                    onClick={() => window.open(`https://wa.me/${bookingDetails.guest_phone.replace(/\D/g, '')}`, '_blank')}
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-slate-500">
              Nessun dettaglio disponibile
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SynopticCalendar;
